import React, { useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { get, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { Card } from '@totalsoft/rocket-ui';
import { isDirty as checkDirty } from '@totalsoft/change-tracking';
import Grid4x4Icon from '@mui/icons-material/Grid4x4';
import WrappedFakeText from 'components/common/WrappedFakeText';
import { useTranslation } from 'react-i18next';
import { fromModel, greaterThan, lessThan, required, shape, stopOnFirstFailure, when } from '@totalsoft/pure-validations';
import { useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import IconButton from 'components/common/buttons/IconButton';
import { useReactiveVar } from '@apollo/client';
import { useToast } from 'hooks/toasts/useToast';
import useHistoryWithContext from 'components/menu/useHistoryWithContext';
import { usePermission } from 'providers/RightsHooks';
import { ADMIN_LEASING_INCOME_SCHEMAS_WRITE } from 'constants/rightsCodes';
import { Prompt } from 'react-router-dom';
import { useMutation, useSubscription } from 'hooks/apollo';
import { Portal, Tooltip } from '@mui/material';
import { isIncomeSchemaEditableVar, negotiationLimitsInAbsoluteValuesVar } from '../variables';
import useQuery from 'hooks/apollo/useQuery';
import { CompactTitle } from 'components/common/Styles';
import { LEASING_INCOME_SCHEMA } from 'features/leasingIncomeSchema/queries';
import { LEASING_INCOME_SCHEMA_SAVED } from 'features/leasingIncomeSchema/subscriptions';
import { SAVE_LEASING_INCOME_SCHEMA } from 'features/leasingIncomeSchema/mutations';
import LeasingIncomeSchemaGeneralDataComponent from './LeasingIncomeSchemaGeneralDataComponent';
import { ServiceContext } from 'providers/ServiceProvider';
import { isValidDateFormat } from 'utils/simple-validations';

const validator = fromModel(model =>
  shape({
    code: required,
    name: required,
    validFrom: [lessThan(model.validTo) |> when(Boolean(model.validTo)), isValidDateFormat] |> stopOnFirstFailure,
    validTo: [greaterThan(model.validFrom) |> when(Boolean(model.validFrom)), isValidDateFormat] |> stopOnFirstFailure
  })
);

const LeasingIncomeSchemaGeneralDataContainer = ({ id }) => {
  const { t } = useTranslation();
  const [activeService] = useContext(ServiceContext);
  const addToast = useToast();
  const historyContext = useHistoryWithContext();

  const canWrite = usePermission(ADMIN_LEASING_INCOME_SCHEMAS_WRITE);
  const isEditable = useReactiveVar(isIncomeSchemaEditableVar);

  const [lens, dirtyInfo, resetLens] = useChangeTrackingLens({ isActive: false });
  const [validation, validate] = useDirtyFieldValidation(validator);

  const [pending, setPending] = useState(false);

  useEffect(() => {
    validate(lens |> get, dirtyInfo);
  }, [validate, lens, dirtyInfo]);

  // #region Data fetching

  const { loading } = useQuery(LEASING_INCOME_SCHEMA, {
    variables: { id },
    skip: !id,
    onCompleted: data => {
      resetLens(data.leasingIncomeSchema);
      isIncomeSchemaEditableVar(data.leasingIncomeSchema.isEditable || false);
      negotiationLimitsInAbsoluteValuesVar(data.leasingIncomeSchema.negotiationLimitsInAbsoluteValues || false);
    }
  });

  // #endregion

  // #region Save

  const [saveLeasingIncomeSchema] = useMutation(SAVE_LEASING_INCOME_SCHEMA, {
    onError: () => {
      setPending(false);
    }
  });

  const handleSave = () => {
    const data = lens |> get;
    if (!validate(data)) return;

    // eslint-disable-next-line no-unused-vars
    const { isEditable, ...sanitizedData } = data;

    setPending(true);

    saveLeasingIncomeSchema({
      variables: { input: sanitizedData }
    });
  };

  useSubscription(LEASING_INCOME_SCHEMA_SAVED, {
    onData: ({ client, data }) => {
      resetLens(data.data.leasingIncomeSchemaSaved.leasingIncomeSchema);
      setPending(false);

      if (!data.data.leasingIncomeSchemaSaved.silent) addToast(t('LeasingIncomeSchema.Saved'), 'success');

      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'leasingIncomeSchemas' });
      if (!id) {
        const newId = data.data.leasingIncomeSchemaSaved.leasingIncomeSchema.id;
        client.writeQuery({
          query: LEASING_INCOME_SCHEMA,
          context: { serviceName: activeService?.name },
          variables: { id: newId },
          data: { leasingIncomeSchema: data.data.leasingIncomeSchemaSaved.leasingIncomeSchema }
        });
        setTimeout(() => {
          historyContext.push(`/leasingIncomeSchemas/${newId}`);
        }, 100);
      }
    }
  });

  // #endregion

  const disabled = useMemo(() => !canWrite || (!isEditable && id) || pending, [canWrite, isEditable, id, pending]);
  const isDirty = useMemo(() => checkDirty(dirtyInfo), [dirtyInfo]);

  return (
    <>
      <Prompt when={isEditable && isDirty} message={t('LeavingWithoutSaving')} />
      <Card icon={Grid4x4Icon} actions={<IconButton type='save' onClick={handleSave} disabled={!isDirty || disabled} />}>
        {loading && <WrappedFakeText />}
        {!loading && <LeasingIncomeSchemaGeneralDataComponent lens={lens} validation={validation} disabled={disabled} />}
      </Card>
      <Portal container={() => document.getElementById('leasing-income-schema-title')}>
        <Tooltip title={lens.name |> get}>
          <span>
            <CompactTitle variant='subtitle1' fontWeight='bold'>
              {t('LeasingIncomeSchema.Title', { name: lens.name |> get })}
            </CompactTitle>
          </span>
        </Tooltip>
      </Portal>
    </>
  );
};

LeasingIncomeSchemaGeneralDataContainer.propTypes = {
  id: PropTypes.number
};

export default LeasingIncomeSchemaGeneralDataContainer;
